// src/contexts/prompt-marketplace-context.tsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useMemo,
} from "react";
import { PromptItem, PromptCategory } from "@/types/marketplace";
import { toast } from "@/components/ui/use-toast";

// Import the raw JSON data
import defaultPromptsDataRaw from "@/data/prompts-compiler";
import categoriesData from "@/data/prompt-categories.json";

// Support new structure: { system: string, prompts: PromptItem[] }
const defaultPromptsData =
  (defaultPromptsDataRaw as any).prompts || defaultPromptsDataRaw;
const genericSystemPrompt = (defaultPromptsDataRaw as any).system || "";

const LOCAL_STORAGE_ALL_PROMPTS_KEY = "marketplaceAllPrompts";

type LoadingStatus = "idle" | "loading" | "succeeded" | "failed";
// export type SortOption = 'highest-rated' | 'most-viewed' | 'newest'; // Removed SortOption

interface PromptMarketplaceContextState {
  allPrompts: PromptItem[];
  filteredPrompts: PromptItem[];
  categories: PromptCategory[];
  loadingStatus: LoadingStatus;
  error: string | null;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  selectedCategory: string; // category id, 'all' for no filter
  setSelectedCategory: (categoryId: string) => void;
  updatePromptAndPersist: (updatedPrompt: PromptItem) => void;
  systemPrompt: string;
}

const PromptMarketplaceContext = createContext<
  PromptMarketplaceContextState | undefined
>(undefined);

// Removed debug console.log to prevent unnecessary logging on every import

export const PromptMarketplaceProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [allPrompts, setAllPrompts] = useState<PromptItem[]>([]);
  const [categories, setCategories] = useState<PromptCategory[]>([]);
  const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all"); // Default to 'all'

  // System prompt should be stable and derived directly from the raw data structure
  const systemPrompt = useMemo(() => genericSystemPrompt, []);

  useEffect(() => {
    const loadMarketplaceData = async () => {
      setLoadingStatus("loading");
      try {
        // Simulate async loading
        await new Promise((resolve) => setTimeout(resolve, 100));

        const baseDefaultPrompts = (defaultPromptsData as PromptItem[]).map(
          (p) => ({
            ...p,
            memberOnly: p.memberOnly ?? false, // Ensure memberOnly has a default
            comingSoon: p.comingSoon ?? false, // Ensure comingSoon has a default
            featured: p.featured ?? false, // Ensure featured has a default
            isCustomized: false,
          }),
        );

        let loadedPrompts: PromptItem[] = [];
        const storedPromptsJson = localStorage.getItem(
          LOCAL_STORAGE_ALL_PROMPTS_KEY,
        );
        let storedPromptsData: PromptItem[] = [];

        if (storedPromptsJson) {
          try {
            storedPromptsData = JSON.parse(storedPromptsJson);
            if (!Array.isArray(storedPromptsData)) {
              console.warn(
                "Invalid prompts data structure in Local Storage, falling back to defaults.",
              );
              storedPromptsData = [];
            }
          } catch (e) {
            console.error(
              "Failed to parse prompts from Local Storage, using defaults:",
              e,
            );
            storedPromptsData = [];
          }
        }

        if (storedPromptsData.length > 0) {
          // Merge stored prompts with defaults to ensure all fields are present
          // and new prompts from json are added.
          const defaultPromptsMap = new Map(
            baseDefaultPrompts.map((p) => [p.id, p]),
          );
          const storedPromptsMap = new Map(
            storedPromptsData.map((p) => [p.id, p]),
          );

          loadedPrompts = baseDefaultPrompts.map((defaultPrompt) => {
            const storedPrompt = storedPromptsMap.get(defaultPrompt.id);
            if (storedPrompt) {
              return {
                ...defaultPrompt, // Start with default (has correct memberOnly, comingSoon, etc.)
                ...storedPrompt, // Override with stored values (custom prompt, title, desc, etc.)
                id: defaultPrompt.id, // Ensure ID from default is used (safety)
                category: storedPrompt.category || defaultPrompt.category,
                isCustomized:
                  storedPrompt.isCustomized !== undefined
                    ? storedPrompt.isCustomized
                    : JSON.stringify(defaultPrompt) !==
                      JSON.stringify(storedPrompt),
                memberOnly: defaultPrompt.memberOnly,
                comingSoon: defaultPrompt.comingSoon,
                featured: defaultPrompt.featured,
              };
            }
            return defaultPrompt; // If not in storage, use the default one
          });
        } else {
          // No valid stored prompts or first load, use the base defaults
          loadedPrompts = baseDefaultPrompts;
        }

        // Ensure all loaded prompts have essential fields, especially if merging logic changes
        loadedPrompts = loadedPrompts.map((p) => ({
          ...p,
          memberOnly: p.memberOnly ?? false,
          comingSoon: p.comingSoon ?? false,
          featured: p.featured ?? false,
          isCustomized: p.isCustomized ?? false,
        }));

        setAllPrompts(loadedPrompts);

        const filteredCategoriesFromJson = (
          categoriesData as PromptCategory[]
        ).filter(
          (cat) => cat.id !== "all" && cat.name.toLowerCase() !== "all prompts",
        );
        setCategories([
          { id: "all", name: "All Prompts" },
          ...filteredCategoriesFromJson,
        ]);
        setError(null);
        setLoadingStatus("succeeded");
      } catch (err) {
        console.error("Failed to load marketplace data:", err);
        setError("Failed to load marketplace data.");
        setLoadingStatus("failed");
      }
    };

    loadMarketplaceData();
  }, []);

  const updatePromptAndPersist = (updatedPrompt: PromptItem) => {
    setAllPrompts((prevPrompts) => {
      const updatedPrompts = prevPrompts.map(
        (p) =>
          p.id === updatedPrompt.id
            ? { ...updatedPrompt, isCustomized: true }
            : p, // Ensure isCustomized is set on update
      );
      // Revert to using localStorage directly as savePromptsToLocalStorage doesn't exist
      try {
        localStorage.setItem(
          LOCAL_STORAGE_ALL_PROMPTS_KEY,
          JSON.stringify(updatedPrompts),
        );
      } catch (e) {
        console.error("Failed to save prompts to Local Storage:", e);
        toast({
          title: "Save Error",
          description:
            "Could not save prompt changes. Changes may be lost on refresh.",
          variant: "destructive",
        });
      }
      return updatedPrompts;
    });
  };

  const filteredPrompts = useMemo(() => {
    let results = [...allPrompts];

    // Filter by search term (title, description, category)
    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      results = results.filter(
        (prompt) =>
          prompt.title.toLowerCase().includes(lowerSearchTerm) ||
          prompt.description.toLowerCase().includes(lowerSearchTerm) ||
          prompt.category.toLowerCase().includes(lowerSearchTerm) ||
          prompt.author?.toLowerCase().includes(lowerSearchTerm),
      );
    }

    // Filter by selected category
    if (selectedCategory !== "all") {
      const categoryName = categories.find(
        (cat) => cat.id === selectedCategory,
      )?.name;
      if (categoryName) {
        results = results.filter((prompt) => prompt.category === categoryName);
      }
    }

    // Sort results: by featured status (true first), then by comingSoon status (false first), then by ID
    results.sort((a, b) => {
      // Sort by featured (true comes before false)
      if (a.featured !== b.featured) {
        return a.featured ? -1 : 1;
      }
      // Then sort by comingSoon (false comes before true)
      if (a.comingSoon !== b.comingSoon) {
        return a.comingSoon ? 1 : -1;
      }
      // Then sort by id (assuming string or number IDs)
      if (a.id < b.id) return -1;
      if (a.id > b.id) return 1;
      return 0;
    });

    return results;
  }, [allPrompts, searchTerm, selectedCategory, categories]);

  return (
    <PromptMarketplaceContext.Provider
      value={{
        allPrompts,
        filteredPrompts,
        categories,
        loadingStatus,
        error,
        searchTerm,
        setSearchTerm,
        selectedCategory,
        setSelectedCategory,
        updatePromptAndPersist,
        systemPrompt,
      }}
    >
      {children}
    </PromptMarketplaceContext.Provider>
  );
};

export const usePromptMarketplace = () => {
  const context = useContext(PromptMarketplaceContext);
  if (context === undefined) {
    throw new Error(
      "usePromptMarketplace must be used within a PromptMarketplaceProvider",
    );
  }
  return context;
};
